/**
 * Los archivos de imagen que cada marca publica: logo y fondos.
 *
 * Van aparte de las primitivas de color porque el modo de falla es distinto.
 * Un color que falta se hereda y pinta el tono de otra marca — sutil, hay que
 * mirarlo con atención. Un logo que falta muestra **la marca equivocada**: un
 * cliente que abre su app con su tema activo ve el logotipo de Silia.
 *
 * ── Estado ────────────────────────────────────────────────────────────────
 *
 * Silia y PrismHR tienen sus archivos propios. Traxión NO los tiene en el
 * repo: sus entradas apuntan a los de Silia y están marcadas
 * `placeholder: true`. Los de eca/lipu/neutral se borraron en SL-1920.
 *
 * Eso es deliberado y es el punto de este archivo: convierte una herencia
 * silenciosa en una declaración explícita que un test vigila. El día que
 * lleguen los archivos, esto es cambiar una ruta y borrar el flag —
 * `brandAssets.test.ts` se pone en verde solo.
 */

import {
  silliaLogo,
  silliaLogoMobile,
  silliaLogoOnLight,
  silliaMark,
  silliaBackground,
  silliaBackgroundWebp,
  silliaModulesBackground,
  silliaModulesBackgroundWebp,
} from '../primitives';

/**
 * Los archivos de una marca, por ROL — no por archivo (SL-1920).
 *
 * Silia tiene dos wordmarks que no son copias: uno oscuro para superficies
 * claras (el sidebar) y uno claro para ir sobre su imagen de fondo (el login).
 * Por eso una marca declara las dos, aunque sean el mismo archivo.
 *
 * Rutas crudas, sin `url()`: el CSS las envuelve (`emitAssetVars`) y los
 * componentes las usan en `<img src>` vía `useBrandAssets()`.
 */
export type BrandAssets = {
  /** Wordmark sobre superficies claras: sidebar expandido. */
  logoOnLight: string;
  /** Wordmark sobre la imagen de fondo de la marca: login, activación, contraseña. */
  logoOnBackground: string;
  /**
   * Isotipo cuadrado: sidebar colapsado, header mobile, marca del asistente y
   * cards de Módulos. La marca del asistente lo usa como MÁSCARA (BrandMark):
   * importa la forma, no los colores.
   */
  mark: string;
  /** Logo del menú mobile. */
  logoMobile: string;
  background: string;
  backgroundWebp: string;
  modulesBackground: string;
  modulesBackgroundWebp: string;
};

/**
 * Una marca y sus archivos.
 *
 * `placeholder` no es una nota al pie: es lo que hace que la deuda sea
 * contable. Sin ese flag, "Traxión usa el logo de Silia" es una frase en un
 * comentario que nadie relee; con él, es un booleano que un test cuenta.
 */
export type BrandAssetSet = {
  assets: BrandAssets;
  /** true mientras algún archivo sea prestado de otra marca. */
  placeholder: boolean;
  /**
   * Cómo se lee derecho el isotipo en la marca del asistente (BrandMark). El de
   * Silia se dibuja acostado y el diseño lo muestra girado 90°; el prisma de
   * PrismHR no se gira.
   */
  markRotation: '0deg' | '90deg';
};

export const siliaAssets: BrandAssetSet = {
  placeholder: false,
  markRotation: '90deg',
  assets: {
    logoOnLight: silliaLogoOnLight,
    logoOnBackground: silliaLogo,
    mark: silliaMark,
    logoMobile: silliaLogoMobile,
    background: silliaBackground,
    backgroundWebp: silliaBackgroundWebp,
    modulesBackground: silliaModulesBackground,
    modulesBackgroundWebp: silliaModulesBackgroundWebp,
  },
};

/**
 * TODO(assets-traxion): reemplazar por los archivos de Traxión y poner
 * `placeholder: false`. Mientras esto siga así, un usuario con Traxión activo
 * ve el logotipo de Silia en el sidebar y en las pantallas de login,
 * activación y reset de contraseña.
 */
export const traxionAssets: BrandAssetSet = {
  placeholder: true,
  // Usa el isotipo de Silia, así que se gira como el de Silia.
  markRotation: siliaAssets.markRotation,
  assets: { ...siliaAssets.assets },
};

const PRISMHR_LOGO = process.env.PUBLIC_URL + '/assets/icons/Logo-PrismHR.svg';
const PRISMHR_MARK = process.env.PUBLIC_URL + '/assets/icons/LogoMobilePrismHR.svg';
const PRISMHR_BACKGROUND = process.env.PUBLIC_URL + '/assets/img/prismhr/prismhr-background.png';
const PRISMHR_BACKGROUND_WEBP =
  process.env.PUBLIC_URL + '/assets/img/prismhr/prismhr-background.webp';

/**
 * PrismHR: logo e isotipo propios (del SVG publicado en prismhr.com) y su fondo
 * (SL-1920). Módulos no tiene imagen propia y usa la del login.
 */
export const prismhrAssets: BrandAssetSet = {
  placeholder: false,
  markRotation: '0deg',
  assets: {
    logoOnLight: PRISMHR_LOGO,
    logoOnBackground: PRISMHR_LOGO,
    mark: PRISMHR_MARK,
    logoMobile: PRISMHR_LOGO,
    background: PRISMHR_BACKGROUND,
    backgroundWebp: PRISMHR_BACKGROUND_WEBP,
    modulesBackground: PRISMHR_BACKGROUND,
    modulesBackgroundWebp: PRISMHR_BACKGROUND_WEBP,
  },
};

/** Envuelve una ruta para consumirla desde CSS. */
export const asCssUrl = (path: string): string => `url(${path})`;

/**
 * Las variables CSS de asset que toda marca declara — solo las que algún CSS
 * LEE (SL-1920 borró las que nadie leía: hero, `--ant-logoUrl`, copias
 * `--silia-*-url` sin lector). El :root las emite con los de Silia y cada
 * bloque de marca con los suyos.
 */
export const assetVarsFor = (set: BrandAssetSet): Record<string, string> => {
  const a = set.assets;
  return {
    // MobileSidebarMenu.css
    '--silia-logo-mobile-url': asCssUrl(a.logoMobile),
    // AuthLayout.css
    '--ant-backgroundUrl': asCssUrl(a.background),
    '--ant-backgroundUrlWebp': asCssUrl(a.backgroundWebp),
    // Modules.css
    '--ant-modulesBackgroundUrl': asCssUrl(a.modulesBackground),
    '--ant-modulesBackgroundUrlWebp': asCssUrl(a.modulesBackgroundWebp),
    // PasswordScreen.css
    '--silia-logo-url': asCssUrl(a.logoOnBackground),
    '--silia-background-url': asCssUrl(a.background),
    '--silia-background-url-webp': asCssUrl(a.backgroundWebp),
    // BrandMark (la marca del asistente, como máscara)
    '--silia-brand-mark-url': asCssUrl(a.mark),
    '--silia-brand-mark-rotation': set.markRotation,
  };
};

/** Emite las declaraciones CSS de una marca, listas para inyectar. */
export const emitAssetVars = (set: BrandAssetSet, indent = '    '): string =>
  Object.entries(assetVarsFor(set))
    .map(([name, value]) => `${indent}${name}: ${value};`)
    .join('\n');
